import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  type IntegrationProvider,
  IntegrationProviderCard,
  IntegrationProviderGrid,
  type IntegrationStatus,
} from '@/components/ui/integration-provider-card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  useDisconnectCalendarAccount,
  useDisconnectInstagramIntegration,
  useDisconnectMetaIntegration,
  useDisconnectWhatsAppAccount,
  useGetConnectionHealth,
  useGetInstagramIntegration,
  useGetMetaIntegration,
  useInitiateGoogleCalendarAuth,
  useInitiateMetaAdsAuth,
  useListCalendarAccounts,
  useListMetaAdsPages,
  useListWhatsAppAccounts,
} from '@/features/integrations/api';
import { useIcalCalendarSettings } from '@/features/integrations/api/ical-calendar/ical-calendar.hook';
import { useCalendarIntegrationEnabled } from '@/features/integrations/api/use-calendar-integration-enabled';
import {
  ConnectionHealthRefresh,
  MetaConnectSelectionDialog,
} from '@/features/integrations/components';
import { useMetaLoginForBusiness } from '@/features/integrations/hooks/use-meta-login-for-business';
import { useWhatsAppEmbeddedSignup } from '@/features/integrations/hooks/use-whatsapp-embedded-signup';
import { startIntegrationOAuth } from '@/lib/open-integration-oauth';
import type { ConnectionProviderId } from '@borradh-workspace/labels';
import { AlertTriangle, CalendarDays } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntegrationOAuthRedirect } from '../hooks/use-integration-oauth-redirect';
import { ChannelStatusPanel } from './channel-status-panel';
import {
  FacebookSettingsDialog,
  GoogleCalendarSettingsDialog,
  InstagramSettingsDialog,
  PayPalSettingsDialog,
  StripeSettingsDialog,
  WhatsAppSettingsDialog,
} from './dialogs';
import { CalendarIntegrationDialog } from './dialogs/calendar-integration-dialog';
import { InstagramChatbotDialog } from './dialogs/instagram-chatbot-dialog';
import { IntegrationWizards } from './integration-wizards';
import { WhatsAppHealthBanner } from './whatsapp-health-banner';

// Base provider data (without status - status is determined from API)
const baseProviders: Omit<IntegrationProvider, 'status'>[] = [
  {
    id: 'meta',
    name: 'Facebook & Instagram',
    description:
      'Connect a Facebook Page and its linked Instagram professional account for ads, messages, and social posts.',
    iconFile: 'fb-icon.svg',
    category: 'social',
    isRecommended: true,
  },
  {
    id: 'whatsapp',
    name: 'WhatsApp',
    description:
      'Connect your WhatsApp Business account to chat with leads and customers.',
    iconFile: 'wa-icon.svg',
    category: 'messaging',
  },
  {
    id: 'calendar',
    name: 'Calendar',
    description: 'Share bookings and block time booked in other calendars.',
    icon: <CalendarDays className="size-6" />,
    category: 'calendar',
  },
];

type DialogType =
  | 'facebook'
  | 'instagram'
  | 'whatsapp'
  | 'calendar'
  | 'google-calendar'
  | 'stripe'
  | 'paypal'
  | 'meta-selection'
  | null;

interface IntegrationsGridProps {
  onConnect?: (providerId: string) => void;
  onEdit?: (providerId: string) => void;
}

export function IntegrationsGrid({ onConnect, onEdit }: IntegrationsGridProps) {
  const [openDialog, setOpenDialog] = useState<DialogType>(null);
  const calendarIntegrationEnabled = useCalendarIntegrationEnabled();
  const { data: icalSettings, isLoading: isLoadingIcal } =
    useIcalCalendarSettings(undefined, calendarIntegrationEnabled);

  // Handle OAuth redirects (wizard sessions from URL params)
  const oauthRedirect = useIntegrationOAuthRedirect();

  // Fetch integration data from API
  const { accounts: calendarAccounts, isLoading: isLoadingCalendar } =
    useListCalendarAccounts({
      queryConfig: { enabled: calendarIntegrationEnabled },
    });
  const {
    integration: metaIntegration,
    isLoading: isLoadingMeta,
    isPendingConfiguration: isMetaPendingConfiguration,
    availableAdAccounts: metaAvailableAdAccounts,
    availablePages: metaAvailablePages,
  } = useGetMetaIntegration();
  const { integration: instagramIntegration, isLoading: isLoadingInstagram } =
    useGetInstagramIntegration();
  const { accounts: whatsappAccounts, isLoading: isLoadingWhatsApp } =
    useListWhatsAppAccounts();
  // One read behind every "something is wrong" surface in the product.
  const { problems: healthProblems } = useGetConnectionHealth();
  const { pages: metaPagesList } = useListMetaAdsPages();
  // The Instagram account linked to a Facebook Page, listed as `<page id>:ig`.
  const linkedInstagramRow = metaPagesList.find((p) => p.id.endsWith(':ig'));
  const hasFacebookPage = useMemo(
    () => metaPagesList.some((p) => p.platform === 'facebook'),
    [metaPagesList]
  );
  // Disconnect hooks
  const { disconnect: disconnectCalendar } = useDisconnectCalendarAccount();
  const { disconnect: disconnectMeta } = useDisconnectMetaIntegration();
  const { disconnect: disconnectInstagram } =
    useDisconnectInstagramIntegration();
  const { disconnect: disconnectWhatsApp } = useDisconnectWhatsAppAccount();

  // OAuth initiation hooks
  const { initiateAuth: initiateGoogleCalendarAuth } =
    useInitiateGoogleCalendarAuth({
      returnTo: '/dashboard/settings/integrations',
    });
  const { initiateAuth: initiateMetaAdsAuth } = useInitiateMetaAdsAuth({
    returnTo: '/dashboard/settings/integrations',
  });
  const { launch: launchWhatsAppSignup } = useWhatsAppEmbeddedSignup();

  const { launch: launchMetaLogin } = useMetaLoginForBusiness({
    // Connecting only stores what the login can reach; the owner picks the
    // business, ad account and Page in the selection dialog.
    onSuccess: () => setOpenDialog('meta-selection'),
  });

  // A connection still waiting for its Page and ad account (a redirect back
  // from Meta, the mobile hand-off, or a dialog closed earlier) reopens the
  // selection once when the page loads.
  const metaSelectionPromptedRef = useRef(false);
  useEffect(() => {
    if (isLoadingMeta || !isMetaPendingConfiguration) return;
    if (metaSelectionPromptedRef.current) return;
    metaSelectionPromptedRef.current = true;
    setOpenDialog((current) => current ?? 'meta-selection');
  }, [isLoadingMeta, isMetaPendingConfiguration]);

  // The FLfB configuration identified by META_LOGIN_CONFIG_ID must include
  // instagram_basic and instagram_content_publish for Page-linked publishing.
  // Existing tokens need a fresh grant after the configuration is updated.
  //
  // Care when reading: for orgs whose IG is still on the legacy standalone
  // instagram_integration row (no FLfB link yet), IG surfaces via the virtual
  // entry emitted by listMetaAdsPages so target-pickers still work, but there
  // is no per-card UI to disconnect it independently until it's migrated.

  const isLoading =
    isLoadingCalendar ||
    isLoadingIcal ||
    isLoadingMeta ||
    isLoadingInstagram ||
    isLoadingWhatsApp;

  // Auto-open Google Calendar settings dialog after OAuth redirect
  useEffect(() => {
    const connected = oauthRedirect.justConnectedIntegration;
    if (
      calendarIntegrationEnabled &&
      (connected === 'calendar' || connected === 'google-calendar') &&
      calendarAccounts.length > 0
    ) {
      setOpenDialog('google-calendar');
      oauthRedirect.clearJustConnected();
    }
  }, [
    oauthRedirect.justConnectedIntegration,
    oauthRedirect.clearJustConnected,
    calendarAccounts,
    calendarIntegrationEnabled,
  ]);

  /**
   * Card status: is it connected at all, and if so is it working.
   *
   * "Connected" is still a question about our own rows — an org with no
   * WhatsApp account has nothing to be unhealthy. "Working" is not: that comes
   * from the probe, so a card cannot claim a connection is fine while the
   * scheduler knows the token is dead. The WhatsApp expiry arithmetic that used
   * to live here (and again, verbatim, in the banner below) is gone with it —
   * a stored expiry date proved nothing anyway, as several dead Instagram
   * tokens with future expiry dates demonstrated.
   */
  const getProviderStatus = useCallback(
    (providerId: string): IntegrationStatus => {
      const isUnhealthy = (provider: ConnectionProviderId) =>
        healthProblems.some((p) => p.provider === provider);

      switch (providerId) {
        case 'meta':
        case 'facebook':
          // Publishing uses the linked Instagram account on the Page token.
          if (!metaIntegration) return 'disconnected';
          if (isUnhealthy('meta_page') || isUnhealthy('meta_ads')) {
            return 'needs_reconnect';
          }
          if (isMetaPendingConfiguration) return 'pending_configuration';
          return 'connected';
        case 'instagram':
          if (!instagramIntegration) return 'disconnected';
          return isUnhealthy('meta_instagram')
            ? 'needs_reconnect'
            : 'connected';
        case 'whatsapp':
          if (whatsappAccounts.length === 0) return 'disconnected';
          return isUnhealthy('meta_whatsapp') ? 'needs_reconnect' : 'connected';
        case 'calendar':
          return calendarAccounts.length > 0 ||
            icalSettings?.enabled ||
            icalSettings?.meetAccount
            ? 'connected'
            : 'disconnected';
        default:
          return 'disconnected';
      }
    },
    [
      metaIntegration,
      isMetaPendingConfiguration,
      instagramIntegration,
      whatsappAccounts,
      calendarAccounts,
      icalSettings,
      healthProblems,
    ]
  );

  // Build full providers list with dynamic status
  const integrationProviders: IntegrationProvider[] = useMemo(() => {
    return baseProviders
      .filter(
        (provider) => calendarIntegrationEnabled || provider.id !== 'calendar'
      )
      .map((provider) => ({
        ...provider,
        status: getProviderStatus(provider.id),
      }));
  }, [calendarIntegrationEnabled, getProviderStatus]);

  const handleConnect = (providerId: string) => {
    // Direct OAuth redirect for disconnected providers
    switch (providerId) {
      case 'meta':
        // One connection for Facebook + Instagram via Facebook Login for
        // Business. Hardcoded — no feature-flag branch, no separate IG path.
        // The FLfB configuration supplies the Instagram publishing grant.
        if (isMetaPendingConfiguration) {
          setOpenDialog('meta-selection');
        } else {
          launchMetaLogin();
        }
        break;
      case 'whatsapp':
        launchWhatsAppSignup();
        break;
      case 'calendar':
        if (calendarIntegrationEnabled) setOpenDialog('calendar');
        break;
      default:
        console.log(`Connect not implemented for: ${providerId}`);
    }
    onConnect?.(providerId);
  };

  const handleEdit = (providerId: string) => {
    // The combined card maps to the Facebook settings dialog.
    if (providerId === 'calendar') {
      if (calendarIntegrationEnabled) setOpenDialog('calendar');
      onEdit?.(providerId);
      return;
    }
    if (providerId === 'meta') {
      setOpenDialog('facebook');
      onEdit?.(providerId);
      return;
    }
    // Open settings dialog for connected providers
    const supportedDialogs: DialogType[] = [
      'facebook',
      'instagram',
      'whatsapp',
      'google-calendar',
      'stripe',
      'paypal',
    ];
    if (supportedDialogs.includes(providerId as DialogType)) {
      setOpenDialog(providerId as DialogType);
    }
    onEdit?.(providerId);
  };

  const handleDisconnect = (providerId: string, accountId?: string) => {
    switch (providerId) {
      case 'meta':
        disconnectMeta();
        break;
      case 'facebook':
        disconnectMeta();
        break;
      case 'instagram':
        disconnectInstagram();
        break;
      case 'whatsapp':
        if (accountId) disconnectWhatsApp(accountId);
        break;
      case 'google-calendar':
        if (accountId) disconnectCalendar(accountId);
        break;
    }
    setOpenDialog(null);
  };

  if (isLoading) {
    return (
      <div className="flex flex-col gap-6">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {[0, 1, 2, 3, 4, 5].map((n) => (
            <Skeleton
              key={`integration-skeleton-${n}`}
              className="h-[220px] w-full"
            />
          ))}
        </div>
      </div>
    );
  }

  // The action-required banner is driven by `connection-health`, not by
  // per-provider column arithmetic.
  //
  // It used to say "your X connection has expired — reconnect to resume
  // publishing" for every problem it found. That sentence is wrong twice over:
  // for the organisations whose Facebook account META has locked, reconnecting
  // cannot work until they clear it with Meta, and for an abandoned onboarding
  // there is nothing to RE-connect. `remedy` now decides both the wording and
  // where the button goes.
  const reconnectHandlers: Partial<Record<ConnectionProviderId, () => void>> = {
    meta_ads: () => initiateMetaAdsAuth(),
    meta_page: () => initiateMetaAdsAuth(),
    meta_instagram: () => {
      void startIntegrationOAuth('integrations/instagram/authorize-url');
    },
    meta_whatsapp: () => launchWhatsAppSignup(),
  };

  // One entry per provider, worst first — several dead Pages should not produce
  // five identical buttons.
  //
  // Stripe is deliberately excluded: this page has no Stripe card and no Stripe
  // controls, so claiming its problems here sends people looking for something
  // that is not on the screen. It surfaces on the payments page, where it can
  // actually be acted on, and on the dashboard home, which is cross-cutting.
  const reconnectItems = healthProblems
    .filter((p) => p.provider !== 'stripe_connect')
    .reduce<
      Array<{
        provider: ConnectionProviderId;
        name: string;
        detail: string | null;
        remedy: string;
        actionUrl: string | null;
        actionLabel: string | null;
        onReconnect?: () => void;
      }>
    >((items, problem) => {
      if (items.some((i) => i.provider === problem.provider)) return items;
      items.push({
        provider: problem.provider,
        name: problem.providerLabel,
        detail: problem.detail,
        remedy: problem.remedy,
        actionUrl: problem.actionUrl,
        actionLabel: problem.actionLabel,
        // No in-app handler for a problem the customer must resolve with the
        // provider — those get the provider's own link instead.
        onReconnect:
          problem.remedy === 'contact_provider'
            ? undefined
            : reconnectHandlers[problem.provider],
      });
      return items;
    }, []);

  return (
    <div className="flex flex-col gap-6">
      {/* WhatsApp token expiring within 7 days (pre-expiry warning) */}
      <WhatsAppHealthBanner
        accounts={whatsappAccounts}
        onReconnect={launchWhatsAppSignup}
      />

      {/* Action required — copy and destination come from the health verdict */}
      {reconnectItems.length > 0 && (
        <Alert variant="destructive">
          <AlertTriangle className="size-4" />
          <AlertTitle>Action required</AlertTitle>
          <AlertDescription className="flex flex-col gap-3">
            <ul className="flex flex-col gap-1">
              {reconnectItems.map((item) => (
                <li key={item.provider}>
                  <span className="font-medium">{item.name}:</span>{' '}
                  {item.detail ?? 'This connection needs attention.'}
                </li>
              ))}
            </ul>
            <div className="flex flex-wrap gap-2">
              {reconnectItems.map((item) =>
                item.onReconnect ? (
                  <Button
                    key={item.provider}
                    variant="outline"
                    size="sm"
                    onClick={item.onReconnect}
                  >
                    {item.remedy === 'finish_setup'
                      ? `Finish ${item.name} setup`
                      : `Reconnect ${item.name}`}
                  </Button>
                ) : item.actionUrl ? (
                  <Button
                    key={item.provider}
                    asChild
                    variant="outline"
                    size="sm"
                  >
                    <a href={item.actionUrl} target="_blank" rel="noreferrer">
                      {item.actionLabel ?? `Resolve with ${item.name}`}
                    </a>
                  </Button>
                ) : null
              )}
              {/* Someone who has just reconnected wants to see this clear now,
                  not at the next six-hourly sweep. */}
              <ConnectionHealthRefresh />
            </div>
          </AlertDescription>
        </Alert>
      )}

      {/* Per channel, on the record each code path uses — the same read
          Claire's fix steps point at (ENG-1944). */}
      <ChannelStatusPanel
        onFix={(action) => {
          switch (action) {
            case 'connect_meta':
              handleConnect('meta');
              break;
            case 'reconnect_meta':
            case 'link_instagram_to_page':
              initiateMetaAdsAuth();
              break;
            case 'reconnect_instagram':
              void startIntegrationOAuth(
                'integrations/instagram/authorize-url'
              );
              break;
            case 'connect_whatsapp':
            case 'reconnect_whatsapp':
              launchWhatsAppSignup();
              break;
          }
        }}
      />

      {/* Grid of integration cards */}
      <IntegrationProviderGrid>
        {integrationProviders.map((provider) => (
          <IntegrationProviderCard
            key={provider.id}
            provider={provider}
            onConnect={handleConnect}
            onEdit={handleEdit}
            disabledReason={
              provider.id === 'whatsapp' &&
              provider.status !== 'connected' &&
              !hasFacebookPage
                ? 'Connect a Facebook Page first'
                : undefined
            }
          />
        ))}
      </IntegrationProviderGrid>
      {calendarIntegrationEnabled && openDialog === 'calendar' && (
        <CalendarIntegrationDialog
          onClose={() => setOpenDialog(null)}
          googleConnected={calendarAccounts.length > 0}
          onGoogleCalendar={() => {
            if (calendarAccounts.length > 0) setOpenDialog('google-calendar');
            else {
              setOpenDialog(null);
              initiateGoogleCalendarAuth();
            }
          }}
        />
      )}

      <MetaConnectSelectionDialog
        open={openDialog === 'meta-selection'}
        onOpenChange={(open) => !open && setOpenDialog(null)}
        onSuccess={() => setOpenDialog(null)}
      />

      {/* Integration Settings Dialogs */}
      <FacebookSettingsDialog
        open={openDialog === 'facebook'}
        onOpenChange={(open) => !open && setOpenDialog(null)}
        // Dialog is only reachable via the merged Meta card, so its Disconnect
        // must drop both integrations — otherwise the leftover
        // instagramIntegration row keeps `getProviderStatus('meta')`
        // returning `connected` and the Edit Connection button never
        // switches back to Connect.
        onDisconnect={() => handleDisconnect('meta')}
        pages={
          metaIntegration?.pages?.map((p) => ({
            id: p.id,
            name: p.pageName ?? 'Connected Page',
            platform: p.platform,
            isDefault: p.id === metaIntegration.defaultPageId,
          })) ?? []
        }
        adAccountName={
          metaIntegration?.adAccountId
            ? (metaIntegration.adAccountName ?? 'Connected Ad Account')
            : null
        }
        facebookProfile={
          metaIntegration?.facebookUserName
            ? {
                name: metaIntegration.facebookUserName,
                email: metaIntegration.facebookUserEmail,
                pictureUrl: metaIntegration.facebookUserPictureUrl,
              }
            : null
        }
        // The combined "Facebook & Instagram" card opens this dialog, so pass
        // the linked-IG identity + per-channel chatbot toggle whenever we have
        // one — the dialog renders its Instagram section only when this prop
        // is non-null (see facebook-settings-dialog.tsx:180).
        instagram={
          // A posting-only connection is not Instagram messaging.
          instagramIntegration && !instagramIntegration.postingOnly
            ? {
                username: instagramIntegration.username,
                name: instagramIntegration.name,
                chatbotEnabled: instagramIntegration.chatbotEnabled ?? false,
              }
            : linkedInstagramRow
              ? {
                  username: linkedInstagramRow.linkedInstagramUsername,
                  name: linkedInstagramRow.linkedInstagramName,
                  chatbotEnabled: linkedInstagramRow.isChatbotActive,
                  // No standalone connection: the account's chatbot flag
                  // lives on its Facebook Page (`<page id>:ig`).
                  pageRowId: linkedInstagramRow.id,
                }
              : null
        }
      />

      <InstagramSettingsDialog
        open={openDialog === 'instagram'}
        onOpenChange={(open) => !open && setOpenDialog(null)}
        onDisconnect={() => handleDisconnect('instagram')}
        onReconnect={() => {
          setOpenDialog(null);
          void startIntegrationOAuth('integrations/instagram/authorize-url');
        }}
        integration={instagramIntegration}
      />

      {instagramIntegration && !instagramIntegration.postingOnly && (
        <InstagramChatbotDialog />
      )}

      <WhatsAppSettingsDialog
        open={openDialog === 'whatsapp'}
        onOpenChange={(open) => !open && setOpenDialog(null)}
        onDisconnect={() => {
          const firstAccount = whatsappAccounts[0];
          if (firstAccount) handleDisconnect('whatsapp', firstAccount.id);
        }}
        accountName={
          whatsappAccounts[0]
            ? whatsappAccounts[0].displayName || whatsappAccounts[0].phoneNumber
            : null
        }
        phoneNumber={whatsappAccounts[0]?.phoneNumber ?? null}
      />

      {calendarIntegrationEnabled && (
        <GoogleCalendarSettingsDialog
          open={openDialog === 'google-calendar'}
          onOpenChange={(open) => !open && setOpenDialog(null)}
          onConnect={initiateGoogleCalendarAuth}
          onDisconnect={(accountId) =>
            handleDisconnect('google-calendar', accountId)
          }
          accounts={calendarAccounts}
        />
      )}

      <StripeSettingsDialog
        open={openDialog === 'stripe'}
        onOpenChange={(open) => !open && setOpenDialog(null)}
        onSave={() => setOpenDialog(null)}
        onDisconnect={() => setOpenDialog(null)}
        accounts={[]}
      />

      <PayPalSettingsDialog
        open={openDialog === 'paypal'}
        onOpenChange={(open) => !open && setOpenDialog(null)}
        onSave={() => setOpenDialog(null)}
        onDisconnect={() => setOpenDialog(null)}
        accounts={[]}
        emails={[]}
      />

      {/* OAuth redirect wizards */}
      <IntegrationWizards
        oauthRedirect={oauthRedirect}
        metaIntegration={metaIntegration}
        metaAvailableAdAccounts={metaAvailableAdAccounts}
        metaAvailablePages={metaAvailablePages}
        isLoadingMeta={isLoadingMeta}
      />
    </div>
  );
}
